<template>
  <button
    class="am3-step-link inline-flex items-center gap-2 text-sm font-medium transition-colors hover:text-brand-dark focus:outline-none"
    :class="[
      disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      color === 'brand' ? 'text-brand' : 'text-primary',
    ]"
    :disabled="disabled"
    :data-test-id="generateDataTestId(dataTestId, 'step-link')"
    @click="handleClick"
  >
    <!-- Left icon -->
    <AM3Icon
      v-if="iconLeft"
      :type="iconLeft"
      size="sm"
      :color="color === 'brand' ? 'brand' : 'strong'"
    />

    <!-- Text content -->
    <span>{{ text }}</span>

    <!-- Right icon -->
    <AM3Icon
      v-if="iconRight"
      :type="iconRight"
      size="sm"
      :color="color === 'brand' ? 'brand' : 'strong'"
    />
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { iconList } from '@/components/am3-generic/base/icon/svgs/_iconList'

/**
 * AM3StepLink component
 *
 * A simple navigation link component for step processes
 */
export default defineComponent({
  name: 'AM3StepLink',

  components: {
    AM3Icon,
  },

  props: {
    // Link text
    text: {
      type: String,
      required: true,
    },

    // Disabled state
    disabled: {
      type: Boolean,
      default: false,
    },

    // Color theme
    color: {
      type: String as PropType<'brand' | 'default'>,
      default: 'brand',
      validator: (value: string) => ['brand', 'default'].includes(value),
    },

    // Left icon
    iconLeft: {
      type: String,
      default: 'arrow-left',
      validator: (value: string) => value === '' || iconList.includes(value),
    },

    // Right icon
    iconRight: {
      type: String,
      default: '',
      validator: (value: string) => value === '' || iconList.includes(value),
    },

    // Data test id
    dataTestId: {
      type: String,
      default: props => props.text || 'step-link',
    },
  },

  emits: ['click'],

  setup(props, { emit }) {
    // Handle click event
    const handleClick = (event: MouseEvent) => {
      if (!props.disabled) {
        emit('click', event)
      }
    }

    return {
      handleClick,
      generateDataTestId,
    }
  },
})
</script>

<style scoped>
.am3-step-link {
  position: relative;
}

.am3-step-link:hover {
  text-decoration: underline;
}

.am3-step-link:focus {
  outline: none;
  text-decoration: underline;
}
</style>
